import { useContext } from "react";
import {
  Button,
  Form,
  OverlayTrigger,
  Spinner,
  Table,
  Tooltip,
} from "react-bootstrap";
import { tasaCero } from "../../../assets/assets";
import BasicModal from "../BasicModal";
import {
  Coverage,
  EXPEDITION_FEE,
  InsuranceCoverage,
} from "../types/tableConstants";
import { useCurrency } from "../useCurrency";
import usePlanComparison from "./usePlanComparison";
import "./styles.scss";
import { FormContext } from "../FormContextProvider";
import { UseType, VehicleClass } from "../types/form";

const PlanComparison = () => {
  const { vehicleData, resetContext } = useContext(FormContext);
  const {
    insureMyCarHandler,
    handleValueChange,
    sendEmailHandler,
    premiumPayment,
    openWhatsApp,
    basicPayment,
    handleClose,
    hasCoverage,
    sendingMail,
    deductibles,
    variables,
    coverage,
    loading,
    premium,
    basic,
    fixed,
    show,
    text,
  } = usePlanComparison();

  const { getCurrencyFormat } = useCurrency();

  const paymentRow = (
    frequency: "monthly" | "annual" | "trimonthly" | "semestral",
    title: string
  ) => {
    return (
      <tr>
        <td>{title}</td>
        <td>
          {loading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            getCurrencyFormat(
              Number(basicPayment[frequency]),
              vehicleData.currency
            )
          )}
        </td>
        {premiumPayment[frequency] && (
          <td>
            {loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              getCurrencyFormat(
                Number(premiumPayment[frequency]),
                vehicleData.currency
              )
            )}
          </td>
        )}
      </tr>
    );
  };

  const getValues = (variable: any, short: string, isBasic = false) => {
    const select = (options: any) => (
      <Form.Select
        className="text-responsive"
        onChange={(e) => handleValueChange(e, short, isBasic)}
        value={
          isBasic
            ? basic[short as keyof InsuranceCoverage].toString()
            : premium[short as keyof InsuranceCoverage].toString()
        }
      >
        {options}
      </Form.Select>
    );
    if (short === "RCO") {
      const value =
        vehicleData.type === UseType.COMMERCIAL ? 10000000 : 5000000;
      return <span>{getCurrencyFormat(value, vehicleData.currency)}</span>;
    }

    if (variable.days) {
      return select(
        variable.days.map((value: string, key: any) => (
          <option value={value} key={key}>
            {`${value} días`}
          </option>
        ))
      );
    } else if (variable[vehicleData.currency]) {
      if (variable[vehicleData.currency].length > 1) {
        return select(
          variable[vehicleData.currency].map((value: any, key: any) => (
            <option value={value} key={key}>
              {getCurrencyFormat(value, vehicleData.currency)}
            </option>
          ))
        );
      } else {
        const value =
          variable[vehicleData.currency] > 0
            ? variable[vehicleData.currency][0]
            : fixed[short as keyof typeof fixed];
        return <span>{getCurrencyFormat(value, vehicleData.currency)}</span>;
      }
    }
  };

  return (
    <div className="text-responsive">
      <h3>Comparación de planes</h3>
      <hr />
      <div className="table-container">
        <Table striped>
          <thead>
            <tr>
              <th>Cobertura</th>
              <th>Básico</th>
              <th>Amplio</th>
            </tr>
          </thead>
          <tbody>
            {coverage.map((key: any) => (
              <tr key={key}>
                <td>
                  <label>{variables[key].name}</label>{" "}
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        {vehicleData.class === VehicleClass.BIKE &&
                        variables[key].bike
                          ? variables[key].bike
                          : variables[key].description}
                      </Tooltip>
                    }
                  >
                    <span className="bi bi-info-circle ms-1"></span>
                  </OverlayTrigger>
                </td>
                <td>
                  {hasCoverage(key, Coverage.BASIC)
                    ? variables[key].included
                      ? "Incluído"
                      : getValues(variables[key], key, true)
                    : "NA"}
                </td>
                <td>
                  {hasCoverage(key, Coverage.CDF)
                    ? variables[key].included
                      ? "Incluído"
                      : getValues(variables[key], key)
                    : "NA"}
                </td>
              </tr>
            ))}
            <tr className="table-div"></tr>
          </tbody>
        </Table>
      </div>
      <div className="table-container">
        {!(vehicleData.class === VehicleClass.BIKE) ? (
          <Table className="deductible-table" striped>
            <thead>
              <tr>
                <th colSpan={2}>Deducibles</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="description">
                  Colisión, Daños de la naturaleza o Vandalismo
                </td>
                <td>
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    getCurrencyFormat(deductibles.DM, vehicleData.currency)
                  )}
                </td>
              </tr>
              <tr>
                <td className="description">Robo Total</td>
                <td>
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    getCurrencyFormat(deductibles.RT, vehicleData.currency)
                  )}
                </td>
              </tr>
              <tr>
                <td className="description">Robo Parcial</td>
                <td>
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    `${deductibles.RP}%`
                  )}
                </td>
              </tr>
              <tr>
                <td className="description">Rotura de Vidrios</td>
                <td>
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    `${deductibles.RV}%`
                  )}
                </td>
              </tr>
              <tr>
                <td className="description">Daños a Terceros</td>
                <td>
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    getCurrencyFormat(deductibles.RCP, vehicleData.currency)
                  )}
                </td>
              </tr>
              <tr className="table-div"></tr>
            </tbody>
          </Table>
        ) : (
          <Table className="deductible-table" striped>
            <tbody>
              <tr>
                <td className="description">Colisión o Daños propios</td>
                <td>
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    getCurrencyFormat(deductibles.DM, vehicleData.currency)
                  )}
                </td>
              </tr>
              <tr>
                <td className="description">Robo</td>
                <td>
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    getCurrencyFormat(deductibles.RT, vehicleData.currency)
                  )}
                </td>
              </tr>
              <tr>
                <td className="description">Daños a Terceros</td>
                <td>
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    getCurrencyFormat(deductibles.RCP, vehicleData.currency)
                  )}
                </td>
              </tr>
            </tbody>
          </Table>
        )}
      </div>
      <div className="table-container">
        <Table striped>
          <thead>
            <tr>
              <th>Prima</th>
              <th>Básico</th>
              {premiumPayment.annual && <th>Amplio</th>}
            </tr>
          </thead>
          <tbody>
            {paymentRow("monthly", "Mensual")}
            {paymentRow("trimonthly", "Trimestral")}
            {paymentRow("semestral", "Semestral")}
            {paymentRow("annual", "Anual")}
          </tbody>
        </Table>
      </div>
      <div className="d-flex justify-content-center align-items-center mt-5 mb-4">
        <div className="text-center">
          Costo de emisión:{" "}
          <strong>
            {getCurrencyFormat(
              EXPEDITION_FEE[vehicleData.currency],
              vehicleData.currency
            )}{" "}
            adicionales al <u>primer pago</u>
          </strong>
        </div>
      </div>
      <div className="d-flex justify-content-center align-items-center mb-5 mt-4">
        <div className="image">
          <img
            alt="Tasa cero a a 3 meses en plan trimestral y 6 meses en plan anual"
            src={tasaCero}
          />
        </div>
      </div>
      <div className="d-flex flex-column justify-content-center align-items-center my-3 w-100">
        <div className="d-flex flex-sm-row flex-column w-100 justify-content-center align-items-xs-center">
          <Button className="my-1 btn-width" onClick={openWhatsApp}>
            Conversar con un Agente
          </Button>
          <Button
            className="mx-sm-3 my-1 btn-width"
            onClick={insureMyCarHandler}
          >
            Asegurar mi vehículo
          </Button>
          <Button
            className="my-1 btn-width"
            onClick={sendEmailHandler}
            disabled={sendingMail}
          >
            Recibir cotización
            {sendingMail && (
              <Spinner className="ms-2" animation="border" size="sm" />
            )}
          </Button>
        </div>
        <div className="d-flex flex-column w-100 justify-content-center align-items-center">
          <hr className="w-100" />
          <Button
            className="btn-width"
            onClick={resetContext}
            variant="secondary"
          >
            Nueva cotización
          </Button>
        </div>
      </div>
      <BasicModal show={show} handleClose={handleClose} text={text} />
    </div>
  );
};

export default PlanComparison;
